import React from "react";
import { useApolloClient } from "@apollo/client";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	RequiredKeys,
	Row,
	Section,
	StyleProps,
	toLocalDateString,
} from "@hex-insights/core";
import { EditMode, Form, SubmitButton } from "@hex-insights/forms";
import { FlexGap } from "../../../../Components/FlexGap";
import { Icon } from "../../../../Components/Icon";
import {
	BillingAdjustment,
	Contract,
	ContractInstallment,
	ContractLineItem,
	ContractUpdateInput,
	Discount,
	DiscountNameFragment,
	PaymentLineItem,
	Service,
	ServiceNameFragment,
	Student,
	StudentNameFragment,
} from "../../../../Utilities";
import { ContractFormat } from "../../../Format";
import { ContractField, ContractForm } from "../../../Forms";
import { StudentLink } from "../../../Links";
import { ContractUpdateButtonWithConfirmation } from "../../../UpdateButtons";
import { ContractUtilities } from "../../../Utilities";
import { ContractInstallmentsList } from "../../ContractInstallment";
import { ContractLineItemsTable } from "../../ContractLineItem";
import { ContractStatusBadge } from "../StatusBadge";
import styles from "./styles.module.css";

export type DetailDisplayContract = Pick<Contract, "id" | "name" | "status" | "effectiveDate"> & {
	student: Pick<Student, "id"> & StudentNameFragment;
	contractLineItems: (Pick<
		ContractLineItem,
		"id" | "displayOrder" | "isDiscount" | "amount" | "dateAdded" | "dateRemoved"
	> & {
		service: (Pick<Service, "id"> & ServiceNameFragment) | null;
		discount: (Pick<Discount, "id"> & DiscountNameFragment) | null;
		childContractLineItems: Pick<ContractLineItem, "id" | "isDiscount" | "amount">[];
	})[];
	contractInstallments: (Pick<ContractInstallment, "id" | "dueDate" | "portion"> & {
		invoices: {
			paymentLineItems: Pick<PaymentLineItem, "amount">[];
			billingAdjustments: Pick<BillingAdjustment, "amount">[];
		}[];
	})[];
};

export type ContractDetailDisplayProps = {
	variant: "admin" | "parent";
	isLoading?: boolean;
	contract: DetailDisplayContract | null | undefined;
} & Partial<ClassNameProps & StyleProps>;

export function ContractDetailDisplay({
	variant,
	isLoading = false,
	contract,
	className,
	style,
}: ContractDetailDisplayProps) {
	const precomputedContractInstallments = React.useMemo(() => {
		if (!contract) {
			return [];
		}
		return ContractUtilities.precomputeContractInstallments(contract);
	}, [contract]);

	const canActivate = contract?.status !== ContractUtilities.ContractStatus.Active;
	const canVoid = contract?.status !== ContractUtilities.ContractStatus.Void;

	return (
		<Conditional>
			<If condition={isLoading && !contract}>
				<div className={className} style={style}>
					Loading...
				</div>
			</If>
			<Else>
				{contract && (
					<Column justify="spaced-start" className={className} style={style}>
						<Section>
							<Section.Body>
								<Row justify="spaced-start" overflow="wrap">
									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["item__label"]}>Student</span>
										<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
											<Icon.Student size={16} /> <StudentLink instance={contract.student} />
										</Row>
									</Column>

									<FlexGap minWidth="1rem" maxWidth="4rem" />

									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["item__label"]}>Effective Date</span>
										<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
											<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
												<Icon.CalendarBlank size={16} /> {ContractFormat.Fields.effectiveDate(contract.effectiveDate)}
											</Row>
											<EffectiveDateEdit contract={contract} />
										</Row>
									</Column>

									<FlexGap minWidth="1rem" maxWidth="4rem" />

									<Column justify="spaced-start" verticalSpacing="0.25rem">
										<span className={styles["item__label"]}>Status</span>
										<Row justify="spaced-start" align="center">
											<ContractStatusBadge status={contract.status} withIcon />
											<If condition={canActivate || canVoid}>
												<Row justify="flex-start" align="center">
													<If condition={canActivate}>
														<ActivateButton contract={contract} />
													</If>
													<If condition={canVoid}>
														<VoidButton contract={contract} />
													</If>
												</Row>
											</If>
										</Row>
									</Column>
								</Row>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H3 noMargin>Services</Heading.H3>
							</Section.Header>
							<Section.Body>
								<ContractLineItemsTable
									variant={variant}
									contract={contract}
									contractLineItems={contract.contractLineItems}
								/>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H3 noMargin>Payment Schedule</Heading.H3>
							</Section.Header>
							<Section.Body>
								<ContractInstallmentsList variant={variant} contractInstallments={precomputedContractInstallments} />
							</Section.Body>
						</Section>
					</Column>
				)}
			</Else>
		</Conditional>
	);
}

type EffectiveDateEditProps = {
	contract: Pick<Contract, "id" | "effectiveDate">;
} & Partial<ClassNameProps & StyleProps>;

function EffectiveDateEdit({ contract, className, style }: EffectiveDateEditProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				variant="link"
				size="small"
				onClick={openModal}
				className={className}
				style={{ padding: "0.25rem", ...style }}
			>
				<Icon.PencilSimple size={16} className="block" />
			</Button>

			<Modal.If condition={isModalOpen}>
				<EffectiveDateEditModal contract={contract} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type EffectiveDateEditModalProps = {
	contract: Pick<Contract, "id" | "effectiveDate">;
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">, "onClose">;

function EffectiveDateEditModal({ contract, ifRef, onClose, className, style }: EffectiveDateEditModalProps) {
	const { formState, onSubmit } = ContractForm.useDetailForm({
		contract: contract,
		fields: { effectiveDate: true },
		editMode: EditMode.WriteOnly,
		onSuccess: onClose,
	});

	return (
		<Modal
			size="small"
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["effective-date-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Effective Date</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} noChangesDialog>
					<Form.Column>
						<ContractField.EffectiveDate formState={formState} />
						<Form.ActionBar>
							<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
								Save
							</SubmitButton>
						</Form.ActionBar>
					</Form.Column>
				</Form>
			</Modal.Body>
		</Modal>
	);
}

const contractStatusUpdateInputInitialFormValues = {};

const activateUpdateInput: ContractUpdateInput = {
	isFinalized: true,
	clearDateVoided: true,
};

type ActivateButtonProps = {
	contract: Pick<Contract, "id" | "status">;
} & Partial<ClassNameProps & StyleProps>;

function ActivateButton({ contract, className, style }: ActivateButtonProps) {
	const clearCache = useClearContractCache();

	const isVoid = contract.status === ContractUtilities.ContractStatus.Void;

	return (
		<ContractUpdateButtonWithConfirmation
			instanceID={contract.id}
			input={activateUpdateInput}
			initialFormValues={contractStatusUpdateInputInitialFormValues}
			confirmationContent={`Are you sure you want to ${isVoid ? "re" : ""}activate this contract?`}
			onSuccess={clearCache}
			variant="link"
			size="small"
			className={className}
			style={style}
		>
			{isVoid ? "Reactivate" : "Finalize"}
		</ContractUpdateButtonWithConfirmation>
	);
}

type VoidButtonProps = {
	contract: Pick<Contract, "id">;
} & Partial<ClassNameProps & StyleProps>;

function VoidButton({ contract, className, style }: VoidButtonProps) {
	const voidUpdateInput = React.useMemo<ContractUpdateInput>(() => ({ dateVoided: toLocalDateString(new Date()) }), []);
	const confirmButtonProps = React.useMemo(() => ({ className: "button--danger-link" }), []);

	const clearCache = useClearContractCache();

	return (
		<ContractUpdateButtonWithConfirmation
			instanceID={contract.id}
			input={voidUpdateInput}
			initialFormValues={contractStatusUpdateInputInitialFormValues}
			confirmationContent="Are you sure you want to void this contract?"
			onSuccess={clearCache}
			size="small"
			className={className}
			style={style}
			buttonClassName="button--danger-link"
			confirmButtonProps={confirmButtonProps}
		>
			Void
		</ContractUpdateButtonWithConfirmation>
	);
}

function useClearContractCache() {
	const client = useApolloClient();

	return React.useCallback(() => {
		client.cache.evict({ id: "ROOT_QUERY", fieldName: "contractConnection" });
	}, [client]);
}
